import React from "react";
import { InputSwitch } from "primereact/inputswitch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "src/components/ui/table";
import { formatCurrency, formatDate } from "../../utils/billing-utils";
import type { CommissionInvoice } from "../types/commissions.types";

function monthYearLabel(month: number, year: number): string {
  const date = new Date(year, month - 1, 1);
  return date.toLocaleString("default", { month: "long", year: "numeric" });
}

interface CommissionInvoicesTableProps {
  invoices: CommissionInvoice[];
  onRowClick: (invoice: CommissionInvoice) => void;
  onToggleIncludeInTotal?: (invoice: CommissionInvoice, includeInTotal: boolean) => void;
}

const CommissionInvoicesTable: React.FC<CommissionInvoicesTableProps> = ({
  invoices,
  onRowClick,
  onToggleIncludeInTotal,
}) => {
  return (
    <div className="rounded-md border border-gray-200 overflow-hidden bg-white">
      <Table>
        <TableHeader>
          <TableRow className="bg-gray-50 border-b border-gray-201">
            <TableHead className="font-medium text-gray-700 py-3 px-3">
              Invoice #
            </TableHead>
            <TableHead className="font-medium text-gray-700 py-3 px-3">
              Company
            </TableHead>
            <TableHead className="font-medium text-gray-700 py-3 px-3">
              Amount
            </TableHead>
            <TableHead className="font-medium text-gray-700 py-3 px-3">
              Month
            </TableHead>
            <TableHead className="font-medium text-gray-700 py-3 px-3">
              Status
            </TableHead>
            <TableHead className="font-medium text-gray-700 py-3 px-3">
              Invoice Date
            </TableHead>
            <TableHead className="font-medium text-gray-700 py-3 px-3">
              Paid Date
            </TableHead>
            <TableHead className="font-medium text-gray-700 py-3 px-3 w-[100px]">
              In total
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {invoices.map((inv) => (
            <TableRow
              key={inv.id}
              className="cursor-pointer hover:bg-gray-50 border-b border-gray-201"
              onClick={() => onRowClick(inv)}
            >
              <TableCell className="font-medium py-4 px-3">
                {inv.invoiceNumber}
              </TableCell>
              <TableCell className="py-4 px-3">
                {inv.companyName}
              </TableCell>
              <TableCell className="py-4 px-3">
                {formatCurrency(inv.amount)}
              </TableCell>
              <TableCell className="py-4 px-3">
                {inv.month != null && inv.year != null
                  ? monthYearLabel(inv.month, inv.year)
                  : "—"}
              </TableCell>
              <TableCell className="py-4 px-3">
                <span
                  className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                    inv.status === "paid"
                      ? "bg-green-100 text-green-800"
                      : "bg-amber-100 text-amber-800"
                  }`}
                >
                  {inv.status}
                </span>
              </TableCell>
              <TableCell className="text-gray-600 py-4 px-3">
                {inv.createdAt ? formatDate(inv.createdAt) : "—"}
              </TableCell>
              <TableCell className="text-gray-600 py-4 px-3">
                {inv.paidDate ? formatDate(inv.paidDate) : "—"}
              </TableCell>
              <TableCell
                className="py-4 px-3"
                onClick={(e) => e.stopPropagation()}
              >
                <InputSwitch
                  checked={inv.includeInTotal !== false}
                  onChange={(e) =>
                    onToggleIncludeInTotal?.(inv, e.value ?? true)
                  }
                  disabled={!onToggleIncludeInTotal}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default CommissionInvoicesTable;
